import React, { memo } from 'react'
import PropTypes from 'prop-types';
import { Tooltip as ReactTooltip } from 'react-tooltip'

import { MultiCheckBox } from '@components/atoms/multi-checkbox'
import { Select } from '@components/atoms/select';
import { minMaxOptions, ouncesData, watersData } from '@config/data';
import { Radio } from '@components/atoms/radio';

const list = [
  {
    name: 'macroCounting',
    label: 'Macro Counting',
    info: <div>Macro counting is a nutritional approach that focuses on tracking the three primary macronutrients—carbohydrates, proteins, and fats—consumed in a day to achieve specific health and fitness goals. By monitoring the intake of these nutrients, individuals can tailor their diets to support weight loss, muscle gain, or improved overall well-being. The process involves calculating daily caloric needs based on factors such as weight, activity level, and desired outcome. Macro counting offers flexibility in food choices while promoting a balanced diet, making it an effective strategy for various lifestyles and goals.
      <br />
      <br />
      To learn more and master macro counting, check out our article <a target='_blank' className='border-b border-red-500 text-red-600' href="https://www.checkmarkfitness.com/blog/how-to-count-macros-like-a-pro" rel="noreferrer">How to Count Macros Like a Pro</a>.
    </div>
  },
  {
    name: 'noProcessedFoods',
    label: 'No Processed Foods',
    info: <div>Cutting out processed foods, particularly those with multiple ingredients you cannot see, can be beneficial for several reasons. When we mention processed foods, we are referring to items like chips, bread, juice, lean pockets, canned soup, etc. Generally, these foods are less optimal choices due to their high levels of added sugars, unhealthy fats, artificial additives, and preservatives, which can contribute to weight gain, inflammation, and chronic diseases.
      <br />
      <br />
      Some high-protein processed foods, such as protein bars (with at least 35% protein), 0% Greek yogurt, beef jerky, and protein powder, can fit into a daily meal plan and offer convenience. Additionally, low-calorie &quot;diet foods&quot; like sugar-free Jello, Halo Top Ice Cream, and Quest Protein chips, when consumed in moderation (less than 10% of daily calories), can make dieting more enjoyable without significant negative impacts on overall nutrition. If you check this box, it is up to you to define what fits your definition.
    </div>
  },
  {
    name: 'intermittentFasting',
    label: 'Intermittent Fasting',
    info: <div>
      Intermittent Fasting (IF) is a dietary approach that alternates between periods of fasting and eating, focusing on when to eat rather than what to eat. This eating pattern has gained popularity for its potential health benefits, such as weight loss, improved insulin sensitivity, and increased mental clarity. There are several popular intermittent fasting schedules:
      <br />
      <br />
      <ol className='list-decimal ml-12'>
        <li>The 16/8 Method: Involves fasting for 16 hours daily and consuming all meals within an 8-hour window, such as between 12 PM and 8 PM. This method is also known as the Leangains Protocol.</li>
        <li>The 5:2 Diet: Consists of eating regularly for five days a week and restricting caloric intake to about 500-600 calories on the remaining two non-consecutive days.</li>
        <li>Eat Stop Eat: Entails a 24-hour fast once or twice a week, during which no solid foods are consumed, but water, tea, and coffee are allowed.</li>
      </ol>
      <br />
      Each of these schedules can be adapted to individual preferences and lifestyles. It is essential to maintain a balanced and nutrient-dense diet during the eating periods to ensure overall health and well-being while practicing intermittent fasting.
    </div>
  },
  {
    name: 'carbTiming',
    label: 'Carb Timing',
    info: <div>Carb timing is a nutritional strategy that involves strategically consuming high glycemic index (GI) carbohydrates before and after lifting heavy weights, while opting for low GI carbs throughout the rest of the day. The glycemic index measures how quickly a carbohydrate-containing food raises blood sugar levels, with high GI foods causing a rapid spike and low GI foods providing a slower, more sustained release. High GI examples include white bread, sugary cereals, and instant mashed potatoes, whereas low GI examples are whole grains, legumes, and non-starchy vegetables. The rationale behind carb timing is that consuming high GI carbs around workouts can help replenish muscle glycogen stores, provide quick energy, and enhance recovery, while low GI carbs consumed throughout the day promote stable blood sugar levels, sustained energy, and improved appetite control. This approach can optimize athletic performance, support muscle growth, and aid in achieving body composition goals.</div>
  },
  {
    name: 'noAlcohol',
    label: 'No Alcohol',
    info: <div>Alcohol consumption can negatively impact well-being and fitness goals by affecting both physical and mental health. Being calorie-dense, alcohol can lead to weight gain, making it challenging to achieve desired body composition. It impairs nutrient absorption by interfering with digestive enzymes and damaging the gastrointestinal tract, potentially causing nutrient deficiencies. Furthermore, alcohol disrupts sleep quality, hindering recovery and cognitive function. It can also reduce muscle protein synthesis, inhibiting muscle growth and repair. Alcohol consumption may contribute to increased stress, anxiety, and depression, affecting motivation, discipline, and the ability to maintain a consistent fitness routine. In summary, moderate to excessive alcohol intake can significantly impede well-being and the attainment of fitness goals. Even quitting for a short term to focus on other fitness goals can have a huge positive impact. </div>
  },
]

export const Diet = memo(({ state, onChange }) => {
  return (
    <>
      <ReactTooltip
        id="tooltip"
        place="top"
        type="dark"
        effect="solid"
      />
      <div className="text-slate-600">What diet goals will you commit to?</div>
      <div className="my-4 flex flex-col space-y-4">
        <MultiCheckBox
          name="diets"
          value={state.diets}
          onChange={onChange}
          list={list}
        />
      </div>
      {state.diets.macroCounting &&
        <>
          <div className="text-slate-600 mb-3">How many cheat meals a week will you have?</div>
          <div className="space-y-2 mb-5">
            <div className="flex space-x-6">
              {minMaxOptions.slice(0, 3).map(x => (
                <Radio
                  name="cheatMeals"
                  label={x.label}
                  id={x.id}
                  value={x.id}
                  onChange={onChange}
                  checked={Number(state.cheatMeals) === Number(x.id)}
                />
              ))}
            </div>
          </div>
        </>
      }
      <div className="text-slate-600 mb-6">How much water will you drink every day?</div>
      <Select
        name="drinkWater"
        label="Water"
        value={state.drinkWater}
        onChange={onChange}
        list={watersData}
      />
      {state.drinkWater !== 1 &&
        <>
          <div className="text-slate-600 mb-6">How many ounces is your regular drinking cup or bottle?</div>
          <Select
            name="regularDrinkCup"
            label="Regular Drink Cup"
            value={state.regularDrinkCup}
            onChange={onChange}
            list={ouncesData}
          />
        </>
      }
    </>
  )
})

Diet.propTypes = {
  state: PropTypes.object,
  onChange: PropTypes.func
}